import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

export type DeclarationDownloadStatus = 'opened' | 'downloaded';

export interface DeclarationDownloadOptions {
  /**
   * Warranty identifier used to fetch the declaration PDF.
   */
  warrantyId: string;
  /**
   * Optional contract number used when naming the downloaded file.
   */
  contractNumber?: string;
  /**
   * Optional custom filename (should include extension).
   */
  fileName?: string;
  /**
   * Delay before revoking the blob URL. Defaults to 1s.
   */
  cleanupDelayMs?: number;
}

export interface DeclarationDownloadResult {
  status: DeclarationDownloadStatus;
}

@Injectable({
  providedIn: 'root',
})
export class DeclarationDownloadService {
  private static readonly DEFAULT_CLEANUP_DELAY_MS = 1000;

  constructor(private readonly http: HttpClient) {}

  async openDeclaration({
    warrantyId,
    contractNumber,
    fileName,
    cleanupDelayMs,
  }: DeclarationDownloadOptions): Promise<DeclarationDownloadResult> {
    if (!warrantyId) {
      throw new Error('A warrantyId is required to generate a declaration.');
    }

    const pdfBlob = await firstValueFrom(
      this.http.get(`/api/warranties/${warrantyId}/declaration`, {
        responseType: 'blob',
      })
    );

    if (typeof window === 'undefined' || typeof document === 'undefined') {
      // Non-browser environment (SSR) – just return a successful response.
      return { status: 'downloaded' };
    }

    const blobUrl = window.URL.createObjectURL(pdfBlob);
    const revokeDelay = cleanupDelayMs ?? DeclarationDownloadService.DEFAULT_CLEANUP_DELAY_MS;
    const revokeBlob = () => window.URL.revokeObjectURL(blobUrl);

    const newTab = window.open(blobUrl, '_blank');

    if (!newTab) {
      // Pop-up blocked – trigger download instead.
      const downloadName = fileName ?? `registration_${contractNumber ?? warrantyId}.pdf`;
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      try {
        link.click();
      } finally {
        document.body.removeChild(link);
        setTimeout(revokeBlob, revokeDelay);
      }

      return { status: 'downloaded' };
    }

    await new Promise(resolve => setTimeout(resolve, 100));
    if (newTab.closed) {
      setTimeout(revokeBlob, revokeDelay);
      return { status: 'downloaded' };
    }

    setTimeout(revokeBlob, revokeDelay);
    return { status: 'opened' };
  }
}
